import { useEffect, useRef, useState } from "react";
import { api, fetchFileBlobUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import { Upload, Loader2, Play, X } from "lucide-react";
import { toast } from "sonner";
import { compressVideo, isVideoFile, posterKey, MAX_VIDEO_DURATION_S } from "../lib/videoCompress";

/**
 * Poster-backed video tile. Tries to fetch the derived `<path>.poster.jpg`
 * thumbnail and shows it behind a play badge; if no poster exists (older
 * videos, or extraction failed at upload) it falls back to a solid dark tile
 * so the play affordance is still clear. Render-only — the parent owns the
 * click/open behaviour.
 */
export function VideoPosterBg({ path }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const pk = posterKey(path);
    if (!pk) return;
    let alive = true;
    let blobUrl;
    (async () => {
      // A missing poster (older videos / extraction failed) is an EXPECTED
      // 404 — fetchFileBlobUrl returns null silently instead of throwing, so
      // the tile just falls back to the dark play badge.
      blobUrl = await fetchFileBlobUrl(pk);
      if (!alive) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      if (blobUrl) setSrc(blobUrl);
      else setFailed(true);
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  if (src && !failed) {
    return <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />;
  }
  return null;
}

/**
 * Upload to /api/files/upload. onUploaded(path, record) is called per file.
 *
 * When `enableVideo` is set, selected videos are re-encoded WhatsApp-style in
 * the browser (≤960px, H.264, ≤30MB, ≤20s) before upload — see lib/videoCompress.
 */
export default function FileUploader({
  yacht_id,
  folder = "misc",
  accept,
  onUploaded,
  label = "Upload",
  multiple = false,
  testid = "file-upload",
  attachment_kind,
  attachment_id,
  enableVideo = false,
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  // null = not compressing; -1 = loading tools; 0..100 = encode progress.
  const [vpct, setVpct] = useState(null);
  // Large-PDF feedback: {pct} while the bytes stream up, then "compressing"
  // while the server shrinks the file (Ghostscript / pikepdf).
  const [pdfPhase, setPdfPhase] = useState(null);

  const fmtMB = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Client-side PDF size guard. We allow up to 25 MB through because the
    // backend compresses PDFs (Ghostscript, 150 DPI image downsampling)
    // before applying the real 10 MB cap. Anything over 25 MB is almost
    // certainly going to remain over 10 MB after compression too, so we
    // reject early. The backend re-enforces the final cap.
    const PDF_CLIENT_HARD_LIMIT = 25 * 1024 * 1024;
    const oversized = files.find(
      (f) => (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")) && f.size > PDF_CLIENT_HARD_LIMIT
    );
    if (oversized) {
      const mb = (oversized.size / (1024 * 1024)).toFixed(1);
      toast.error(`PDF "${oversized.name}" is ${mb} MB — too large to compress under the 10 MB limit. Please reduce it before uploading.`);
      if (ref.current) ref.current.value = "";
      return;
    }

    setBusy(true);
    let uploaded = 0;
    let compressedToasts = 0;
    try {
      for (const original of files) {
        let f = original;
        let posterBlob = null;
        if (isVideoFile(original)) {
          if (!enableVideo) {
            toast.error("Videos can't be uploaded here.");
            continue;
          }
          try {
            const result = await compressVideo(original, (p) => setVpct(p));
            f = result.video;
            posterBlob = result.poster;
          } catch (err) {
            toast.error(err?.message || "Video could not be processed.");
            continue;
          } finally {
            setVpct(null);
          }
        }
        const fd = new FormData();
        fd.append("file", f);
        if (posterBlob) fd.append("poster", posterBlob, "poster.jpg");
        if (yacht_id) fd.append("yacht_id", yacht_id);
        fd.append("folder", folder);
        if (enableVideo) fd.append("allow_video", "true");
        if (attachment_kind && attachment_id) {
          fd.append("attachment_kind", attachment_kind);
          fd.append("attachment_id", attachment_id);
        }
        const isPdf = f.type === "application/pdf" || (f.name || "").toLowerCase().endsWith(".pdf");
        const bigPdf = isPdf && f.size > 1.5 * 1024 * 1024;
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: bigPdf ? (ev) => {
            if (!ev.total) return;
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setPdfPhase(pct >= 100 ? "compressing" : { pct });
          } : undefined,
        });
        setPdfPhase(null);
        // Tell the user how much the server-side PDF compression saved —
        // only when it saved something meaningful (>5% and >100 KB).
        if (isPdf && data.size && f.size - data.size > Math.max(0.05 * f.size, 100 * 1024)) {
          const savedPct = Math.round(100 * (1 - data.size / f.size));
          toast.success(`"${f.name}" compressed ${fmtMB(f.size)} → ${fmtMB(data.size)} (${savedPct}% smaller)`, { duration: 6000 });
          compressedToasts += 1;
        }
        onUploaded?.(data.storage_path, data);
        uploaded += 1;
      }
      // Only celebrate when something actually landed — a fully-rejected
      // batch (e.g. a too-long video) must NOT show "Upload complete".
      // The compression toast already confirms success for shrunk PDFs.
      if (uploaded > 0 && compressedToasts === 0) toast.success("Upload complete");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Upload failed");
    } finally {
      setBusy(false);
      setVpct(null);
      setPdfPhase(null);
      if (ref.current) ref.current.value = "";
    }
  };

  const btnLabel = vpct === -1
    ? "Preparing…"
    : vpct != null
    ? `Processing ${vpct}%`
    : pdfPhase === "compressing"
    ? "Compressing PDF…"
    : pdfPhase?.pct != null
    ? `Uploading ${pdfPhase.pct}%`
    : busy
    ? "Uploading…"
    : label;

  return (
    <>
      <input
        ref={ref}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handle}
        data-testid={`${testid}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => ref.current?.click()}
        data-testid={testid}
      >
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {btnLabel}
      </Button>
      {enableVideo && (
        <span className="sr-only">Videos are re-encoded to ≤30MB, max {MAX_VIDEO_DURATION_S}s.</span>
      )}
    </>
  );
}

/**
 * Authenticated <video> player — fetches the file via the auth `api` client
 * (so cookies/Bearer are sent), wraps it in a same-origin blob URL and plays
 * it inline. Used for video items in the gallery / lightbox.
 */
export function AuthVideo({ path, className = "", controls = true, autoPlay = false }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!path) return;
    let alive = true;
    let blobUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  if (!path) return null;
  if (err) return <div className={`bg-zinc-100 grid place-items-center text-xs text-zinc-400 ${className}`}>Video unavailable</div>;
  if (!src) return <div className={`bg-zinc-900 grid place-items-center ${className}`}><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  return <video src={src} className={className} controls={controls} autoPlay={autoPlay} playsInline />;
}

/**
 * A dark square tile with a play badge that opens the video full-screen in a
 * dialog. Reused by the maintenance photo buckets where there's no metadata
 * lightbox to fold the video into.
 */
export function VideoThumb({ path, className = "", testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`relative grid place-items-center overflow-hidden bg-zinc-900 hover:bg-zinc-800 transition-colors ${className}`}
        data-testid={testid}
        aria-label="Play video"
      >
        <VideoPosterBg path={path} />
        {/* Subtle scrim so the play badge stays legible over bright posters. */}
        <span className="absolute inset-0 bg-black/15" />
        <span className="relative grid place-items-center h-9 w-9 rounded-full bg-white/90 text-zinc-900 shadow">
          <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
        </span>
        <span className="absolute bottom-1 right-1 text-[9px] uppercase tracking-wider bg-black/70 text-white px-1 py-0.5 rounded">Video</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl w-[95vw] p-0 bg-black border-zinc-800" data-testid="video-lightbox">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute right-3 top-3 z-20 rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          {open && <AuthVideo path={path} className="w-full max-h-[85vh] bg-black" autoPlay />}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Authenticated image fetcher returning blob URL
export function AuthImage({ path, alt = "", className = "" }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!path) return;
    let alive = true;
    let blobUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  if (!path) return null;
  if (err) return <div className={`bg-zinc-100 ${className}`} />;
  if (!src) return <div className={`bg-zinc-100 animate-pulse ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

/**
 * Inline first-page thumbnail for a PDF stored in our auth-protected files
 * endpoint. Fetches the PDF as a blob via the authenticated `api` client,
 * renders page 1 to a canvas using pdfjs-dist (dynamically imported on first
 * use to keep the main bundle slim), and clicks-through to open the full
 * document in a new tab. Falls back to a plain `<AuthFileLink>` if pdfjs
 * fails to load or the file isn't a valid PDF — never a hard error.
 *
 * NOTE: pdfjs requires a Web Worker — the matching `pdf.worker.min.mjs`
 * file lives in `frontend/public/` so it's served at the app's origin
 * (no CORS, no version mismatch). Keep the public copy in sync with the
 * `pdfjs-dist` version in package.json or worker init will silently fail.
 */
export function PdfPreview({ path, width = 320, testid }) {
  const canvasRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | failed
  const blobRef = useRef(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;

    (async () => {
      try {
        // Dynamic import keeps pdfjs out of the initial bundle — it only
        // ships once a user actually views a PDF receipt.
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

        const r = await api.get(`/files/${path}`, { responseType: "arraybuffer" });
        if (cancelled) return;
        const doc = await pdfjs.getDocument({ data: r.data }).promise;
        if (cancelled) { doc.destroy(); return; }

        const page = await doc.getPage(1);
        if (cancelled) { doc.destroy(); return; }

        // Pick a scale so the rendered canvas is approximately `width` CSS
        // pixels wide, then bump by devicePixelRatio for crisp rendering on
        // retina displays.
        const baseViewport = page.getViewport({ scale: 1 });
        const cssScale = width / baseViewport.width;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: cssScale * dpr });

        const canvas = canvasRef.current;
        if (!canvas) { doc.destroy(); return; }
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;

        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        // Build a blob for the click-to-open handler so we don't refetch.
        blobRef.current = new Blob([r.data], { type: "application/pdf" });
        if (!cancelled) setState("ready");
        page.cleanup();
        doc.destroy();
      } catch (err) {
        // Common failure modes: file isn't a PDF, worker URL 404'd, or
        // pdfjs version mismatch. Either way fall back to the plain link.
        // eslint-disable-next-line no-console
        console.warn("PdfPreview render failed:", err);
        if (!cancelled) setState("failed");
      }
    })();

    return () => { cancelled = true; };
  }, [path, width]);

  const openInNewTab = () => {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    window.open(url, "_blank", "noopener,noreferrer");
    // Revoke after a delay so the new tab has time to load the URL. 60s is
    // generous — browsers typically copy the blob into the tab within 1s.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (!path) return null;
  if (state === "failed") {
    // Defer to the regular auth file link so the user still has a way out.
    return <AuthFileLink path={path} testid={testid}>{path.split("/").pop() || "Open PDF"}</AuthFileLink>;
  }

  return (
    <button
      type="button"
      onClick={openInNewTab}
      disabled={state !== "ready"}
      className={`relative block rounded-md border border-zinc-200 overflow-hidden bg-zinc-50 hover:border-zinc-400 transition-colors ${state !== "ready" ? "cursor-default" : "cursor-zoom-in"}`}
      data-testid={testid}
      title={state === "ready" ? "Open full PDF in a new tab" : "Loading PDF preview…"}
    >
      <canvas ref={canvasRef} />
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-50">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      )}
      {state === "ready" && (
        <span className="absolute bottom-1.5 right-1.5 text-[10px] uppercase tracking-wider bg-black/70 text-white px-1.5 py-0.5 rounded">
          PDF · page 1
        </span>
      )}
    </button>
  );
}

/**
 * Authenticated link to a stored file (PDF, docx, …) that the user can OPEN.
 *
 * Why not a plain `<a href="/api/files/...">`? Backend file routes require the
 * `access_token` (or `session_token`) cookie which is `SameSite=None;Secure`.
 * Cross-origin browser navigations don't always replay third-party cookies,
 * and even when they do the response is rendered as a download (no inline
 * viewer because we don't set the right Content-Disposition). To dodge both
 * issues we fetch the bytes via axios (which sends cookies + Bearer headers
 * correctly), build a same-origin blob URL, and open *that* in a new tab —
 * which Chrome and Safari both render inline for PDFs/images.
 *
 * The blob URL is revoked when the component unmounts so we don't leak.
 */
export function AuthFileLink({ path, children, className = "", testid }) {
  const [busy, setBusy] = useState(false);
  const blobRef = useRef(null);

  useEffect(() => {
    // Cleanup any blob URL we created on unmount.
    return () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, []);

  if (!path) return null;

  const open = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.get(`/files/${path}`, { responseType: "blob" });
      // Re-wrap with the server-reported MIME so the browser knows how to
      // render it (axios sometimes flattens to application/octet-stream
      // which forces a download instead of an inline preview).
      const mime = r.headers["content-type"] || r.data.type || "application/octet-stream";
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      // Track the latest URL so the cleanup effect can revoke it. We don't
      // revoke the previous one here because the previously-opened tab may
      // still be reading it.
      blobRef.current = url;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Unable to open file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <a
      href="#"
      onClick={open}
      className={`text-zinc-900 underline underline-offset-2 hover:text-black ${busy ? "opacity-50 pointer-events-none" : ""} ${className}`}
      data-testid={testid}
    >
      {children}
    </a>
  );
}
